import {useMediaQuery} from 'react-responsive';
import {isMobile} from 'react-device-detect';
import {size} from 'shared/Breakpoints';

const SMALL_WIDTH_THRESHOLD = 695;
const SMALL_WIDTH_THRESHOLD_936 = 936;
const MEDIUM_WIDTH_THRESHOLD = 767;
const LARGE_WIDTH_THRESHOLD = 1250;

// We are using these as some of the premium tablets requests for desktop
// website which bypasses our isMobile checks.
// up to this wide the sidebars float over the content instead of sitting
// beside it, unless the screen is small enough for the mobile layout
const SIDEBAR_OVERLAY_MAX_WIDTH = 1440;
// the right sidebar floats over the content up to this wide, as it
// squeezes the page too much sitting beside it on these screens
const CART_OVERLAY_MAX_WIDTH = 1680;

export const isTouchDevice =
    isMobile ||
    (typeof window !== 'undefined' &&
        !!window.matchMedia?.('(pointer: coarse)').matches);

// an open sidebar covers the page, it floats over the content, or takes the
// whole screen on small ones
export const isSidebarCoveringPage = () =>
    typeof window !== 'undefined' &&
    !!document.body &&
    document.body.clientWidth <= SIDEBAR_OVERLAY_MAX_WIDTH;

// the sidebars float over the content, rather than the small screen layout
export const isSidebarOverlayLayout = () =>
    isSidebarCoveringPage() && window.innerWidth > SMALL_WIDTH_THRESHOLD;

export const isCartCoveringPage = () =>
    typeof window !== 'undefined' &&
    !!document.body &&
    document.body.clientWidth <= CART_OVERLAY_MAX_WIDTH;

export const isCartOverlayLayout = () =>
    isCartCoveringPage() && window.innerWidth > SMALL_WIDTH_THRESHOLD;

export const isDeviceLarge = () =>
    useMediaQuery({
        minWidth: 1650,
    });

export const isDeviceMediumToLarge = () =>
    useMediaQuery({
        minWidth: 1367,
        maxWidth: 1720,
    });

export const isDeviceMid = (hasMinResolution = true) =>
    useMediaQuery({
        minWidth: 1024,
        maxWidth: 1366,
        minResolution: hasMinResolution ? '1.5dppx' : undefined,
    });

export const isDeviceMedium = () =>
    useMediaQuery({
        minWidth: MEDIUM_WIDTH_THRESHOLD + 1,
        maxWidth: LARGE_WIDTH_THRESHOLD,
    });

export const isDeviceSmallMedium = () =>
    useMediaQuery({
        minWidth: SMALL_WIDTH_THRESHOLD + 1,
        maxWidth: MEDIUM_WIDTH_THRESHOLD,
    });

export const isDeviceSmall = () =>
    useMediaQuery({
        maxWidth: SMALL_WIDTH_THRESHOLD,
    });

export const isDeviceSmall936 = () =>
    useMediaQuery({
        maxWidth: SMALL_WIDTH_THRESHOLD_936,
    });

export const useCustomMediaQuery = ({
    minWidth,
    maxWidth,
}: {
    minWidth?: number;
    maxWidth?: number;
}) =>
    useMediaQuery({
        minWidth,
        maxWidth,
    });

export const useTabletSize = () => {
    const isSmallDevice = isDeviceMedium();
    const isSmallMediumDevice = isDeviceSmallMedium();

    return isSmallDevice || isSmallMediumDevice;
};

export const useIsMobileLandscape = () => {
    return useMediaQuery({
        query: `(max-width: ${size.md - 1}px) and (orientation: landscape) and (pointer: coarse)`,
    });
};

export const useIsMobilePortrait = () => {
    return useMediaQuery({
        query: `(max-width: ${size.sm - 1}px) and (orientation: portrait) and (pointer: coarse)`,
    });
};

// phones and tablets held in portrait, or any other
// screen narrower than a tablet, like a small desktop window. these get the
// floating bottom menu instead of the left sidebar
export const useIsHandheldPortrait = () => {
    const isPortrait = useMediaQuery({orientation: 'portrait'});
    const isNarrow = useMediaQuery({maxWidth: size.sm - 1});

    return isTouchDevice ? isPortrait : isNarrow;
};

export const useIsTabletPortrait = () => {
    return useMediaQuery({
        query: `(min-width: ${size.sm}px) and (max-width: 1180px) and (pointer: coarse) and (orientation: portrait)`,
    });
};

export const useIsMobileLandscapeAndPortraitDevices = () => {
    const isMobilePortrait = useMediaQuery({
        query: `(max-width: ${size.sm - 1}px) and (orientation: portrait) and (pointer: coarse)`,
    });
    const isMobileLandscape = useMediaQuery({
        query: `(max-width: ${size.md - 1}px) and (orientation: landscape) and (pointer: coarse)`,
    });
    const isTabletPortrait = useMediaQuery({
        query: `(min-width: ${size.sm}px) and (max-width: 1180px) and (pointer: coarse) and (orientation: portrait)`,
    });

    return isMobilePortrait || isMobileLandscape || isTabletPortrait;
};

export const useIsPortraitDevices = () => {
    const isMobilePortrait = useMediaQuery({
        query: `(max-width: ${size.sm - 1}px) and (orientation: portrait) and (pointer: coarse)`,
    });
    const isTabletPortrait = useMediaQuery({
        query: `(min-width: ${size.sm}px) and (max-width: 1180px) and (pointer: coarse) and (orientation: portrait)`,
    });

    return isMobilePortrait || isTabletPortrait;
};

export const useIsTouchDevices = () => {
    const isMobilePortrait = useMediaQuery({
        query: `(max-width: ${size.sm - 1}px) and (orientation: portrait) and (pointer: coarse)`,
    });
    const isMobileLandscape = useMediaQuery({
        query: `(max-width: ${size.md}px) and (orientation: landscape) and (pointer: coarse)`,
    });
    const isTabletPortrait = useMediaQuery({
        query: `(min-width: ${size.sm}px) and (max-width: 1180px) and (pointer: coarse) and (orientation: portrait)`,
    });
    const isTabletLandscape = useMediaQuery({
        query: `(min-width: 1180px) and (pointer: coarse) and (orientation: landscape)`,
    });

    return (
        isMobilePortrait ||
        isMobileLandscape ||
        isTabletPortrait ||
        isTabletLandscape
    );
};
